import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {EventData} from '@/types/event';
import {Location} from '@/schemas/sub-schemas';
import {getDistanceFromLatLonInKm} from '@/utils';

export interface EventFormState {
  title: string;
  description: string;
  online: boolean;
  date: string | null;
  time: string | null;
  sports: string | string[];
  ageGroups: string[];
  recurring: number[];
  end: string | null;
  endTime: string | null;
  accessType: string;
  address: string;
  location: Location | null;
  myGroup: string;
  maxParticipants: number | null;
  company: string | undefined;
  companyRole: string | null;
  isPaid: boolean;
  price: number;
  currency: string;
  imprintLink?: string;
  termsOfUseLink?: string;
  cancellationPolicyLink?: string;
  _id?: string;
  trainerEvent?: boolean;
}

interface EventState {
  myEvents: EventData[];
  eventForm: EventFormState;
}

export const initialState: EventState = {
  myEvents: [],
  eventForm: {
    title: '',
    description: '',
    online: false,
    date: null,
    time: null,
    sports: '',
    ageGroups: [],
    recurring: [],
    end: null,
    endTime: null,
    accessType: 'PUBLIC',
    address: '',
    location: null,
    myGroup: '',
    maxParticipants: null,
    companyRole: null,
    isPaid: false,
    price: 0,
    company: undefined,
    imprintLink: undefined,
    termsOfUseLink: undefined,
    cancellationPolicyLink: undefined,
    currency: 'EUR',
    _id: undefined,
    trainerEvent: false
  }
};

export const calculateDistance = (
  event: EventData,
  location: Location
): EventData => {
  const newDistance = getDistanceFromLatLonInKm(
    location.lat,
    location.lng,
    event.geolocation.coordinates[1],
    event.geolocation.coordinates[0]
  );

  return {
    ...event,
    dist: {
      ...event.dist,
      calculated: newDistance
    }
  };
};

export const sortEventsByDistance = (events: EventData[]) => {
  return events.sort((a, b) => {
    const distA =
      a.dist && a.dist.calculated !== null ? a.dist.calculated : Infinity;
    const distB =
      b.dist && b.dist.calculated !== null ? b.dist.calculated : Infinity;
    return distA - distB;
  });
};

export const sortEventsByStartDate = (events: EventData[]) => {
  return events.sort((a, b) => {
    const startDateA = new Date(a.start).getTime();
    const startDateB = new Date(b.start).getTime();
    return startDateA - startDateB;
  });
};

const eventSlice = createSlice({
  name: 'event',
  initialState: initialState,
  reducers: {
    setEventsInRedux(state, action: PayloadAction<EventData[]>) {
      const uniqueEvents = Array.from(
        new Map(action.payload.map((event) => [event._id, event])).values()
      );
      state.myEvents = uniqueEvents;
    },

    updateMyEvents(
      state,
      action: PayloadAction<{
        event: EventData;
        participantId: string;
        userLocation?: Location;
      }>
    ) {
      const {event} = action.payload;

      const {participantId} = action.payload;

      const isParticipant =
        event.participants.findIndex((p) => p._id === participantId) >= 0;

      const isManager =
        event.managers.findIndex((m) => m._id === participantId) >= 0;

      const isModerator =
        event.moderators?.findIndex((m) => m._id === participantId) >= 0;

      let newEvents = [...state.myEvents];
      const isInMyEvents = newEvents.findIndex((e) => e._id === event._id);

      if (isParticipant) {
        if (isInMyEvents < 0) {
          newEvents.push(event);
          newEvents = sortEventsByStartDate(newEvents);
        } else {
          newEvents.splice(isInMyEvents, 1, event);
        }
      } else if (isInMyEvents >= 0) {
        if (isManager || isModerator) {
          newEvents.splice(isInMyEvents, 1, event);
        } else {
          newEvents.splice(isInMyEvents, 1);
        }
      }
      return {
        ...state,
        myEvents: newEvents
      };
    },

    addEventToEvents(state, action: PayloadAction<{event: EventData}>) {
      const {event} = action.payload;
      state.myEvents.push(event);
      state.myEvents = sortEventsByStartDate(state.myEvents);
    },

    removeEventFromEvents(state, action: PayloadAction<string>) {
      state.myEvents = state.myEvents.filter(
        (event) => event._id !== action.payload
      );
    },

    updateEventInRedux(state, action: PayloadAction<{event: EventData}>) {
      const {event} = action.payload;
      const index = state.myEvents.findIndex((e) => e._id === event._id);

      if (index !== -1) {
        state.myEvents[index] = event;
        state.myEvents = sortEventsByStartDate(state.myEvents);
      }
    },

    removeAllEvents(state) {
      state.myEvents = [];
    },

    setEventFormData(state, action: PayloadAction<Partial<EventFormState>>) {
      state.eventForm = {
        ...state.eventForm,
        ...action.payload
      };
    },

    resetEventFormData(state) {
      state.eventForm = {
        ...initialState.eventForm
      };
    }
  }
});

export const {
  setEventsInRedux,
  addEventToEvents,
  removeEventFromEvents,
  updateEventInRedux,
  removeAllEvents,
  updateMyEvents,
  setEventFormData,
  resetEventFormData
} = eventSlice.actions;
export default eventSlice.reducer;
